import React, { useEffect, useMemo, useState } from 'react';
import TheatreServices from '../services/externalServices/TicketServices/theatreServices';

function TheatersMaster() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({
    city: '', name: '', code: '', imageUrl: '',
    location: { latitude: '', longitude: '', address: '' },
    paymentType: 'exclusive', isEnabled: true,
    maxCount: 0, maxTimeLimit: 0,
    approvalRequired: false, approvalAuthority: '',
    infoMessage: '', alertMessage: '',
  });

  const normalized = (s) => String(s||'').trim().toLowerCase();
  const keyOf = (t) => `${normalized(t.name)}|${normalized(t.city)}`;
  const keys = useMemo(()=> new Set(list.map(keyOf)), [list]);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const res = await TheatreServices.listTheatres();
      const arr = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : res?.data?.data || []);
      setList(arr);
    } catch (e) { setError('Failed to load theatres'); } finally { setLoading(false); }
  };

  useEffect(()=>{ load(); }, []);

  const validateCreate = () => {
    if (!form.name || !form.city) return 'Name and City are required';
    const k = keyOf(form);
    if (keys.has(k)) return 'Duplicate theatre (same Name + City) exists';
    return '';
  };

  const handleCreate = async () => {
    const v = validateCreate();
    if (v) { alert(v); return; }
    try {
      const payload = {
        city: form.city, name: form.name, code: form.code,
        imageUrl: form.imageUrl || '',
        location: {
          latitude: Number(form.location.latitude)||0,
          longitude: Number(form.location.longitude)||0,
          address: form.location.address||''
        },
        paymentType: form.paymentType,
        isEnabled: !!form.isEnabled,
        maxCount: Number(form.maxCount)||0,
        maxTimeLimit: Number(form.maxTimeLimit)||0,
        approvalRequired: !!form.approvalRequired,
        approvalAuthority: form.approvalAuthority || '',
        infoMessage: String(form.infoMessage||'').split('\n').map(s=>s.trim()).filter(Boolean),
        alertMessage: String(form.alertMessage||'').split('\n').map(s=>s.trim()).filter(Boolean),
      };
      await TheatreServices.createTheatre(payload);
      setCreateOpen(false);
      setForm({ city: '', name: '', code: '', imageUrl: '', location: { latitude: '', longitude: '', address: '' }, paymentType: 'exclusive', isEnabled: true, maxCount: 0, maxTimeLimit: 0, approvalRequired: false, approvalAuthority: '', infoMessage: '', alertMessage: '' });
      await load();
    } catch (e) { alert('Create failed'); }
  };

  const handleSaveEdit = async () => {
    if (!editItem?._id) return;
    try {
      const normalizeMsgs = (v) => Array.isArray(v) ? v : String(v||'').split('\n').map(s=>s.trim()).filter(Boolean);
      const update = { ...editItem };
      update.infoMessage = normalizeMsgs(update.infoMessage);
      update.alertMessage = normalizeMsgs(update.alertMessage);
      await TheatreServices.updateTheatre(editItem._id, update);
      setEditItem(null);
      await load();
    } catch (e) { alert('Update failed'); }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-2">Theaters</h2>
      <p className="text-sm text-gray-600 mb-4">Master & Config → Masters → Theaters</p>

      <div className="mb-3 flex gap-2">
        <button className="px-4 py-2 bg-black text-white rounded" onClick={()=> setCreateOpen(true)}>+ Add Theatre</button>
        <button className="px-3 py-2 bg-gray-200 rounded" onClick={load}>Refresh</button>
      </div>

      {loading ? <div>Loading...</div> : error ? <div className="text-red-600">{error}</div> : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left">
                {['Name','Code','City','Address','Lat','Lng','Image','Payment Type','Max Count','Max Time Limit','Approval Req','Authority','Info Msgs','Alert Msgs','Enabled','Actions'].map(h => <th key={h} className="px-2 py-1 border-b">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {list.map(t => (
                <tr key={t._id}>
                  <td className="px-2 py-1 border-b">{t.name}</td>
                  <td className="px-2 py-1 border-b">{t.code || ''}</td>
                  <td className="px-2 py-1 border-b">{t.city}</td>
                  <td className="px-2 py-1 border-b">{t.location?.address || ''}</td>
                  <td className="px-2 py-1 border-b">{t.location?.latitude}</td>
                  <td className="px-2 py-1 border-b">{t.location?.longitude}</td>
                  <td className="px-2 py-1 border-b">{t.imageUrl ? (<img alt="img" src={t.imageUrl} style={{ width: 40, height: 24, objectFit: 'contain' }} />) : ''}</td>
                  <td className="px-2 py-1 border-b">{t.paymentType || ''}</td>
                  <td className="px-2 py-1 border-b">{t.maxCount ?? 0}</td>
                  <td className="px-2 py-1 border-b">{t.maxTimeLimit ?? 0}</td>
                  <td className="px-2 py-1 border-b">{t.approvalRequired ? 'Yes' : 'No'}</td>
                  <td className="px-2 py-1 border-b">{t.approvalAuthority || ''}</td>
                  <td className="px-2 py-1 border-b align-top">
                    {(() => {
                      const msgs = t.infoMessage;
                      if (Array.isArray(msgs)) {
                        if (msgs.length === 0) return '';
                        return (
                          <div className="max-w-xs whitespace-pre-wrap text-xs">
                            {msgs.map((m,i) => {
                              if (typeof m === 'string') return <div key={i}>• {m}</div>;
                              if (m && typeof m === 'object') {
                                const parts = Object.entries(m).map(([k,v])=>`${k}: ${v}`);
                                return <div key={i}>• {parts.join(', ')}</div>;
                              }
                              try { return <div key={i}>• {JSON.stringify(m)}</div>; } catch { return <div key={i}>• {String(m)}</div>; }
                            })}
                          </div>
                        );
                      }
                      if (!msgs) return '';
                      if (typeof msgs === 'string') return <div className="text-xs max-w-xs">{msgs}</div>;
                      try { return <div className="text-xs max-w-xs">{JSON.stringify(msgs)}</div>; } catch { return <div className="text-xs max-w-xs">{String(msgs)}</div>; }
                    })()}
                  </td>
                  <td className="px-2 py-1 border-b align-top">
                    {(() => {
                      const msgs = t.alertMessage;
                      if (Array.isArray(msgs)) {
                        if (msgs.length === 0) return '';
                        return (
                          <div className="max-w-xs whitespace-pre-wrap text-xs">
                            {msgs.map((m,i) => {
                              if (typeof m === 'string') return <div key={i}>• {m}</div>;
                              if (m && typeof m === 'object') {
                                const parts = Object.entries(m).map(([k,v])=>`${k}: ${v}`);
                                return <div key={i}>• {parts.join(', ')}</div>;
                              }
                              try { return <div key={i}>• {JSON.stringify(m)}</div>; } catch { return <div key={i}>• {String(m)}</div>; }
                            })}
                          </div>
                        );
                      }
                      if (!msgs) return '';
                      if (typeof msgs === 'string') return <div className="text-xs max-w-xs">{msgs}</div>;
                      try { return <div className="text-xs max-w-xs">{JSON.stringify(msgs)}</div>; } catch { return <div className="text-xs max-w-xs">{String(msgs)}</div>; }
                    })()}
                  </td>
                  <td className="px-2 py-1 border-b">{t.isEnabled ? 'Yes' : 'No'}</td>
                  <td className="px-2 py-1 border-b">
                    <button className="text-blue-600" onClick={()=> setEditItem(t)}>Edit</button>
                  </td>
                </tr>
              ))}
              {list.length === 0 && <tr><td className="px-2 py-2" colSpan={16}>No theatres</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {createOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded w-full max-w-xl max-h-[85vh] overflow-auto">
            <h3 className="font-semibold mb-3">Add Theatre</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs mb-1">Name</label>
                <input className="border rounded px-3 py-2 w-full" value={form.name} onChange={e=> setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs mb-1">Code</label>
                <input className="border rounded px-3 py-2 w-full" value={form.code} onChange={e=> setForm({ ...form, code: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs mb-1">City</label>
                <input className="border rounded px-3 py-2 w-full" value={form.city} onChange={e=> setForm({ ...form, city: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs mb-1">Image URL</label>
                <input className="border rounded px-3 py-2 w-full" value={form.imageUrl} onChange={e=> setForm({ ...form, imageUrl: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs mb-1">Approval Required</label>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={!!form.approvalRequired} onChange={e=> setForm({ ...form, approvalRequired: e.target.checked })} />
                  <span>Yes</span>
                </div>
              </div>
              <div>
                <label className="block text-xs mb-1">Approval Authority</label>
                <input className="border rounded px-3 py-2 w-full" value={form.approvalAuthority} onChange={e=> setForm({ ...form, approvalAuthority: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs mb-1">Address</label>
                <input className="border rounded px-3 py-2 w-full" value={form.location.address} onChange={e=> setForm({ ...form, location: { ...form.location, address: e.target.value } })} />
              </div>
              <div>
                <label className="block text-xs mb-1">Latitude</label>
                <input className="border rounded px-3 py-2 w-full" type="number" value={form.location.latitude} onChange={e=> setForm({ ...form, location: { ...form.location, latitude: e.target.value } })} />
              </div>
              <div>
                <label className="block text-xs mb-1">Longitude</label>
                <input className="border rounded px-3 py-2 w-full" type="number" value={form.location.longitude} onChange={e=> setForm({ ...form, location: { ...form.location, longitude: e.target.value } })} />
              </div>
              <div>
                <label className="block text-xs mb-1">Payment Type</label>
                <select className="border rounded px-3 py-2 w-full" value={form.paymentType} onChange={e=> setForm({ ...form, paymentType: e.target.value })}>
                  {['inclusive','exclusive'].map(x=> <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs mb-1">Max Count</label>
                <input className="border rounded px-3 py-2 w-full" type="number" value={form.maxCount} onChange={e=> setForm({ ...form, maxCount: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs mb-1">Max Time Limit (sec)</label>
                <input className="border rounded px-3 py-2 w-full" type="number" value={form.maxTimeLimit} onChange={e=> setForm({ ...form, maxTimeLimit: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs mb-1">Info Messages (one per line)</label>
                <textarea className="border rounded px-3 py-2 w-full" rows={3} value={form.infoMessage} onChange={e=> setForm({ ...form, infoMessage: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs mb-1">Alert Messages (one per line)</label>
                <textarea className="border rounded px-3 py-2 w-full" rows={3} value={form.alertMessage} onChange={e=> setForm({ ...form, alertMessage: e.target.value })} />
              </div>
              <div className="flex items-center gap-2 mt-6">
                <input type="checkbox" checked={!!form.isEnabled} onChange={e=> setForm({ ...form, isEnabled: e.target.checked })} />
                <span>Enabled</span>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button className="px-3 py-1 bg-gray-200 rounded" onClick={()=> setCreateOpen(false)}>Cancel</button>
              <button className="px-3 py-1 bg-black text-white rounded" onClick={handleCreate}>Create</button>
            </div>
          </div>
        </div>
      )}

      {editItem && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded w-full max-w-xl max-h-[85vh] overflow-auto">
            <h3 className="font-semibold mb-3">Edit Theatre</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs mb-1">Name</label>
                <input className="border rounded px-3 py-2 w-full" value={editItem.name||''} onChange={e=> setEditItem({ ...editItem, name: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs mb-1">Code</label>
                <input className="border rounded px-3 py-2 w-full" value={editItem.code||''} onChange={e=> setEditItem({ ...editItem, code: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs mb-1">City</label>
                <input className="border rounded px-3 py-2 w-full" value={editItem.city||''} onChange={e=> setEditItem({ ...editItem, city: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs mb-1">Image URL</label>
                <input className="border rounded px-3 py-2 w-full" value={editItem.imageUrl||''} onChange={e=> setEditItem({ ...editItem, imageUrl: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs mb-1">Approval Required</label>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={!!editItem.approvalRequired} onChange={e=> setEditItem({ ...editItem, approvalRequired: e.target.checked })} />
                  <span>Yes</span>
                </div>
              </div>
              <div>
                <label className="block text-xs mb-1">Approval Authority</label>
                <input className="border rounded px-3 py-2 w-full" value={editItem.approvalAuthority||''} onChange={e=> setEditItem({ ...editItem, approvalAuthority: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs mb-1">Address</label>
                <input className="border rounded px-3 py-2 w-full" value={editItem.location?.address||''} onChange={e=> setEditItem({ ...editItem, location: { ...(editItem.location||{}), address: e.target.value } })} />
              </div>
              <div>
                <label className="block text-xs mb-1">Latitude</label>
                <input className="border rounded px-3 py-2 w-full" type="number" value={editItem.location?.latitude||''} onChange={e=> setEditItem({ ...editItem, location: { ...(editItem.location||{}), latitude: e.target.value } })} />
              </div>
              <div>
                <label className="block text-xs mb-1">Longitude</label>
                <input className="border rounded px-3 py-2 w-full" type="number" value={editItem.location?.longitude||''} onChange={e=> setEditItem({ ...editItem, location: { ...(editItem.location||{}), longitude: e.target.value } })} />
              </div>
              <div>
                <label className="block text-xs mb-1">Payment Type</label>
                <select className="border rounded px-3 py-2 w-full" value={editItem.paymentType||'exclusive'} onChange={e=> setEditItem({ ...editItem, paymentType: e.target.value })}>
                  {['inclusive','exclusive'].map(x=> <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs mb-1">Max Count</label>
                <input className="border rounded px-3 py-2 w-full" type="number" value={editItem.maxCount ?? 0} onChange={e=> setEditItem({ ...editItem, maxCount: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs mb-1">Max Time Limit (sec)</label>
                <input className="border rounded px-3 py-2 w-full" type="number" value={editItem.maxTimeLimit ?? 0} onChange={e=> setEditItem({ ...editItem, maxTimeLimit: e.target.value })} />
              </div>
              <div className="flex items-center gap-2 mt-6">
                <input type="checkbox" checked={!!editItem.isEnabled} onChange={e=> setEditItem({ ...editItem, isEnabled: e.target.checked })} />
                <span>Enabled (Uncheck = mark for deletion)</span>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs mb-1">Info Messages (one per line)</label>
                <textarea className="border rounded px-3 py-2 w-full" rows={3}
                  value={Array.isArray(editItem.infoMessage) ? editItem.infoMessage.join('\n') : (typeof editItem.infoMessage === 'string' ? editItem.infoMessage : JSON.stringify(editItem.infoMessage||''))}
                  onChange={e=> setEditItem({ ...editItem, infoMessage: e.target.value })}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs mb-1">Alert Messages (one per line)</label>
                <textarea className="border rounded px-3 py-2 w-full" rows={3}
                  value={Array.isArray(editItem.alertMessage) ? editItem.alertMessage.join('\n') : (typeof editItem.alertMessage === 'string' ? editItem.alertMessage : JSON.stringify(editItem.alertMessage||''))}
                  onChange={e=> setEditItem({ ...editItem, alertMessage: e.target.value })}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button className="px-3 py-1 bg-gray-200 rounded" onClick={()=> setEditItem(null)}>Cancel</button>
              <button className="px-3 py-1 bg-black text-white rounded" onClick={handleSaveEdit}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TheatersMaster;
